import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSnackbar } from 'notistack';
import { useFechaOperativa } from '../../contexts/FechaOperativaContext';
import granjaService, { GranjaKpis, GranjaLoteKpi } from '../../services/granjaService';
import { toLocalDateStringYMD } from '../../utils/formatters';

/** Paleta compartida donut + barras apiladas (teal / emerald / steel / lime). */
const CALIBRE_COLORS = [
  '#14b8a6',
  '#10b981',
  '#64748b',
  '#84cc16',
  '#0d9488',
  '#059669',
  '#475569',
  '#65a30d',
];

const KpiHighlight: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  accent?: string;
}> = ({ title, value, subtitle, accent = '#1976d2' }) => (
  <Card
    sx={{
      bgcolor: '#fff',
      borderRadius: 3,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      height: '100%',
      borderTop: `4px solid ${accent}`,
    }}
  >
    <CardContent sx={{ py: 2.5, px: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
        {title}
      </Typography>
      <Typography variant="h3" sx={{ fontWeight: 700, color: accent, lineHeight: 1.15, my: 0.5 }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const avgPostura = (lotes: GranjaLoteKpi[]): number => {
  const cabezas = lotes.reduce((a, l) => a + (l.cabezasVivas || 0), 0);
  const huevos = lotes.reduce((a, l) => a + (l.huevosRecolectados || 0), 0);
  if (cabezas <= 0) return 0;
  return Math.round((huevos / cabezas) * 10000) / 100;
};

const avgMortalidad = (lotes: GranjaLoteKpi[]): number => {
  const iniciales = lotes.reduce((a, l) => a + (l.cabezasIniciales || 0), 0);
  if (iniciales <= 0) {
    if (lotes.length === 0) return 0;
    return (
      Math.round(
        (lotes.reduce((a, l) => a + (l.mortalidadAcumuladaPct || 0), 0) / lotes.length) * 100,
      ) / 100
    );
  }
  const vivas = lotes.reduce((a, l) => a + (l.cabezasVivas || 0), 0);
  return Math.round(Math.max(0, ((iniciales - vivas) / iniciales) * 10000)) / 100;
};

const formatDiaLabel = (fechaIso: string): string => {
  const d = new Date(fechaIso);
  if (Number.isNaN(d.getTime())) return fechaIso.slice(0, 10);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
};

const formatCant = (n: number): string =>
  Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 2 });

const formatPct = (n: number): string =>
  `${Number(n || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

/** % diario: cantidad ÷ gallinas vivas. El total del período usa la suma de gallinas-día. */
const pctSobreCabezas = (cantidad: number, cabezas: number): number =>
  cabezas > 0 ? Math.round((cantidad / cabezas) * 10000) / 100 : 0;

const GranjaPanel: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { fechaYmd } = useFechaOperativa();
  const diaOperativo = fechaYmd ?? toLocalDateStringYMD();
  const [kpis, setKpis] = useState<GranjaKpis | null>(null);
  const [loading, setLoading] = useState(true);
  const [loteFiltro, setLoteFiltro] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await granjaService.getKpis(diaOperativo, diaOperativo);
        setKpis(data);
      } catch {
        enqueueSnackbar('Error al cargar KPIs', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [enqueueSnackbar, diaOperativo]);

  const lotes = kpis?.lotes ?? [];
  const lotesVista = useMemo(
    () => (loteFiltro ? lotes.filter((l) => l.codigoLote === loteFiltro) : lotes),
    [lotes, loteFiltro],
  );

  const posturaPct = avgPostura(lotesVista);
  const mortalidadPct = avgMortalidad(lotesVista);
  const filtroLabel = loteFiltro ? `Lote ${loteFiltro}` : 'Todos los lotes';

  const alertasSanitarias = lotes.filter((l) => l.alertaSanitaria);
  const alertasStock = (kpis?.autonomiaAlimento ?? []).filter((a) => a.alertaStockMinimo);

  const pieData = (kpis?.ratioCalibres ?? [])
    .filter((r) => r.cantidad > 0)
    .map((r) => ({
      name: `${r.calibre} ${r.color}`.trim(),
      value: r.cantidad,
      codigo: r.codigoArticulo,
    }));

  const calibreKeys = useMemo(() => {
    const keys: { key: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const dia of kpis?.clasificacionUltimos7Dias ?? []) {
      for (const it of dia.items) {
        const key = it.codigoArticulo || `${it.calibre}|${it.color}`;
        if (seen.has(key)) continue;
        seen.add(key);
        keys.push({ key, label: `${it.calibre} ${it.color}`.trim() || key });
      }
    }
    if (keys.length === 0) {
      for (const r of kpis?.ratioCalibres ?? []) {
        const key = r.codigoArticulo || `${r.calibre}|${r.color}`;
        if (seen.has(key)) continue;
        seen.add(key);
        keys.push({ key, label: `${r.calibre} ${r.color}`.trim() || key });
      }
    }
    return keys;
  }, [kpis]);

  const stackedData = useMemo(
    () =>
      (kpis?.clasificacionUltimos7Dias ?? []).map((dia) => {
        const row: Record<string, string | number> = {
          dia: formatDiaLabel(dia.fecha),
        };
        for (const { key } of calibreKeys) {
          const item = dia.items.find(
            (it) => (it.codigoArticulo || `${it.calibre}|${it.color}`) === key,
          );
          row[key] = item?.cantidad ?? 0;
        }
        return row;
      }),
    [kpis, calibreKeys],
  );

  const postura7Data = useMemo(
    () =>
      (kpis?.posturaMortandadUltimos7Dias ?? []).map((dia) => {
        const lotesDia = loteFiltro
          ? dia.lotes.filter(
              (l) => l.codigoLote.trim().toLowerCase() === loteFiltro.trim().toLowerCase(),
            )
          : dia.lotes;
        const huevos = lotesDia.reduce((a, l) => a + l.huevos, 0);
        const mortandad = lotesDia.reduce((a, l) => a + l.mortandad, 0);
        const cabezas = lotesDia.reduce((a, l) => a + l.cabezasVivas, 0);
        return {
          dia: formatDiaLabel(dia.fecha),
          huevos,
          mortandad,
          cabezas,
          porcentajePostura: pctSobreCabezas(huevos, cabezas),
          porcentajeMortandad: pctSobreCabezas(mortandad, cabezas),
        };
      }),
    [kpis, loteFiltro],
  );

  const postura7Totales = useMemo(() => {
    const huevos = postura7Data.reduce((a, d) => a + d.huevos, 0);
    const mortandad = postura7Data.reduce((a, d) => a + d.mortandad, 0);
    const cabezas = postura7Data.reduce((a, d) => a + d.cabezas, 0);
    return {
      huevos,
      mortandad,
      porcentajePostura: pctSobreCabezas(huevos, cabezas),
      porcentajeMortandad: pctSobreCabezas(mortandad, cabezas),
    };
  }, [postura7Data]);

  const clasifTabla = useMemo(() => {
    const dias = stackedData.map((row) => String(row.dia));
    const filas = calibreKeys.map(({ key, label }) => {
      const valores = stackedData.map((row) => Number(row[key] ?? 0));
      return {
        key,
        label,
        valores,
        total: valores.reduce((a, b) => a + b, 0),
      };
    });
    const totalesDia = dias.map((_, i) => filas.reduce((a, f) => a + f.valores[i], 0));
    return {
      dias,
      filas,
      totalesDia,
      total: totalesDia.reduce((a, b) => a + b, 0),
    };
  }, [stackedData, calibreKeys]);

  const posturaData = lotes.map((l) => ({
    name: l.codigoLote,
    postura: l.porcentajePostura,
    cantidad: l.huevosRecolectados,
    selected: loteFiltro === l.codigoLote,
  }));

  const barFill = (selected: boolean, accent: string, muted: string) => {
    if (selected) return accent;
    if (loteFiltro) return muted;
    return accent;
  };

  const handleBarClick = (payload: { name?: string } | undefined) => {
    const codigo = payload?.name;
    if (!codigo) return;
    setLoteFiltro((prev) => (prev === codigo ? null : codigo));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Stack spacing={2}>
        <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Panel Granja
              </Typography>
              <Typography variant="body2" color="text.secondary">
                KPIs operativos del {diaOperativo.split('-').reverse().join('/')} (fecha operativa).
                Accesos rápidos para el operario.
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 1,
                }}
              >
                {[
                  { label: 'Recolección', path: '/granja/recoleccion' },
                  { label: 'Clasificación', path: '/granja/clasificacion' },
                  { label: 'Aves', path: '/granja/aves' },
                  { label: 'Alimento', path: '/granja/alimento' },
                  { label: 'Despacho producción', path: '/granja/carga' },
                  { label: 'Movimientos', path: '/granja/movimientos' },
                  { label: 'Informes', path: '/granja/informes' },
                  { label: 'Config', path: '/granja/config' },
                ].map((a) => (
                  <Button
                    key={a.path}
                    variant="outlined"
                    onClick={() => navigate(a.path)}
                    sx={{ minHeight: 52, fontWeight: 600 }}
                  >
                    {a.label}
                  </Button>
                ))}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <KpiHighlight
            title="Postura diaria"
            value={loading ? '…' : `${posturaPct.toFixed(2)}%`}
            subtitle={`${filtroLabel} · huevos / gallina`}
            accent="#0d9488"
          />
          <KpiHighlight
            title="Mortalidad acumulada"
            value={loading ? '…' : `${mortalidadPct.toFixed(2)}%`}
            subtitle={filtroLabel}
            accent="#d32f2f"
          />
        </Box>

        {loteFiltro ? (
          <Alert
            severity="info"
            onClose={() => setLoteFiltro(null)}
            sx={{ py: 0.5 }}
          >
            Filtrando indicadores por lote <strong>{loteFiltro}</strong>. Tocá de nuevo la barra o
            cerrá para ver todos.
          </Alert>
        ) : null}

        {alertasSanitarias.map((l) => (
          <Alert key={l.codigoLote} severity="error">
            Alerta sanitaria lote {l.codigoLote}: bajas del día {l.bajasDia} sobre{' '}
            {l.cabezasVivas} cabezas (tolerancia {l.toleranciaPct}%).
          </Alert>
        ))}

        {alertasStock.map((a) => (
          <Alert key={a.codigoArticulo} severity="warning">
            Stock bajo {a.codigoArticulo}
            {a.descripcion ? ` (${a.descripcion})` : ''}: {a.stockDisponible} (mín.{' '}
            {a.stockMinimo}). Autonomía ~{a.diasAutonomia} días.
          </Alert>
        ))}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Cantidades / postura por lote
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Barras: huevos recolectados (izq.) y % postura (der.). Tocá un lote para filtrar.
              </Typography>
              {loading ? (
                <Typography variant="body2">Cargando…</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={posturaData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis
                      yAxisId="cantidad"
                      orientation="left"
                      allowDecimals={false}
                      tickFormatter={(v) => Number(v).toLocaleString('es-AR')}
                    />
                    <YAxis
                      yAxisId="postura"
                      orientation="right"
                      unit="%"
                      domain={[0, 'auto']}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) =>
                        name === '% Postura'
                          ? [`${Number(value).toFixed(2)}%`, '% Postura']
                          : [Number(value).toLocaleString('es-AR'), 'Recolectado']
                      }
                    />
                    <Legend />
                    <Bar
                      yAxisId="cantidad"
                      dataKey="cantidad"
                      name="Recolectado"
                      cursor="pointer"
                    >
                      {posturaData.map((entry) => (
                        <Cell
                          key={`cant-${entry.name}`}
                          fill={barFill(entry.selected, '#1976d2', '#90caf9')}
                          onClick={() => handleBarClick({ name: entry.name })}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Bar>
                    <Bar
                      yAxisId="postura"
                      dataKey="postura"
                      name="% Postura"
                      cursor="pointer"
                    >
                      {posturaData.map((entry) => (
                        <Cell
                          key={`pct-${entry.name}`}
                          fill={barFill(entry.selected, '#0d9488', '#99f6e4')}
                          onClick={() => handleBarClick({ name: entry.name })}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Ratio de calibres
              </Typography>
              {loading ? (
                <Typography variant="body2" color="text.secondary">
                  Cargando…
                </Typography>
              ) : pieData.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin clasificaciones en el período.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="48%"
                      innerRadius={58}
                      outerRadius={92}
                      paddingAngle={3}
                      stroke="#fff"
                      strokeWidth={2}
                      label={({ name, percent }) =>
                        `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                      }
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={CALIBRE_COLORS[i % CALIBRE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        `${Number(value).toLocaleString('es-AR')} huevos`,
                        name,
                      ]}
                    />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
              Postura y mortandad — últimos 7 días
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {filtroLabel}. Postura:{' '}
              <strong>{loading ? '…' : `${formatCant(postura7Totales.huevos)} huevos`}</strong>
              {' · '}
              Mortandad:{' '}
              <strong>{loading ? '…' : `${formatCant(postura7Totales.mortandad)} aves`}</strong>
              . Eje izquierdo: huevos. Eje derecho: aves.
            </Typography>
            {loading ? (
              <Typography variant="body2">Cargando…</Typography>
            ) : postura7Data.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sin datos de postura ni mortandad en los últimos 7 días.
              </Typography>
            ) : (
              <Stack spacing={2}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={postura7Data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" />
                    <YAxis
                      yAxisId="huevos"
                      orientation="left"
                      allowDecimals={false}
                      tickFormatter={(v) => Number(v).toLocaleString('es-AR')}
                    />
                    <YAxis
                      yAxisId="mortandad"
                      orientation="right"
                      allowDecimals={false}
                      tickFormatter={(v) => Number(v).toLocaleString('es-AR')}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        Number(value).toLocaleString('es-AR', { maximumFractionDigits: 2 }),
                        name,
                      ]}
                    />
                    <Legend />
                    <Bar
                      yAxisId="huevos"
                      dataKey="huevos"
                      name="Postura (huevos)"
                      fill="#0d9488"
                    />
                    <Bar
                      yAxisId="mortandad"
                      dataKey="mortandad"
                      name="Mortandad (aves)"
                      fill="#d32f2f"
                    />
                  </BarChart>
                </ResponsiveContainer>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: '#fafafa',
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            minWidth: 150,
                          }}
                        />
                        {postura7Data.map((fila) => (
                          <TableCell
                            key={fila.dia}
                            align="right"
                            sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            {fila.dia}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow hover>
                        <TableCell
                          sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}
                        >
                          Postura (huevos)
                        </TableCell>
                        {postura7Data.map((fila) => (
                          <TableCell key={`h-${fila.dia}`} align="right">
                            {formatCant(fila.huevos)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCant(postura7Totales.huevos)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell
                          sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}
                        >
                          Mortandad (aves)
                        </TableCell>
                        {postura7Data.map((fila) => (
                          <TableCell key={`m-${fila.dia}`} align="right">
                            {formatCant(fila.mortandad)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCant(postura7Totales.mortandad)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell
                          sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}
                        >
                          % Postura
                        </TableCell>
                        {postura7Data.map((fila) => (
                          <TableCell key={`p-${fila.dia}`} align="right">
                            {formatPct(fila.porcentajePostura)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatPct(postura7Totales.porcentajePostura)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell
                          sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}
                        >
                          % Mortandad
                        </TableCell>
                        {postura7Data.map((fila) => (
                          <TableCell key={`pm-${fila.dia}`} align="right">
                            {formatPct(fila.porcentajeMortandad)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatPct(postura7Totales.porcentajeMortandad)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Clasificación por calibre — últimos 7 días
            </Typography>
            {loading ? (
              <Typography variant="body2">Cargando…</Typography>
            ) : stackedData.length === 0 || calibreKeys.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Sin datos de clasificación en los últimos 7 días.
              </Typography>
            ) : (
              <Stack spacing={2}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stackedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {calibreKeys.map(({ key, label }, i) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        name={label}
                        stackId="calibres"
                        fill={CALIBRE_COLORS[i % CALIBRE_COLORS.length]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: '#fafafa',
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            minWidth: 130,
                          }}
                        >
                          Calibre
                        </TableCell>
                        {clasifTabla.dias.map((dia) => (
                          <TableCell
                            key={dia}
                            align="right"
                            sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            {dia}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clasifTabla.filas.map((fila) => (
                        <TableRow key={fila.key} hover>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              position: 'sticky',
                              left: 0,
                              bgcolor: '#fff',
                              zIndex: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {fila.label}
                          </TableCell>
                          {fila.valores.map((valor, i) => (
                            <TableCell key={`${fila.key}-${clasifTabla.dias[i]}`} align="right">
                              {formatCant(valor)}
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCant(fila.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}
                        >
                          Total
                        </TableCell>
                        {clasifTabla.totalesDia.map((valor, i) => (
                          <TableCell key={`tot-${clasifTabla.dias[i]}`} align="right" sx={{ fontWeight: 700 }}>
                            {formatCant(valor)}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatCant(clasifTabla.total)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Stack spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Resumen
              </Typography>
              <Typography variant="body2">
                Recolectado:{' '}
                <strong>{loading || !kpis ? '…' : kpis.totalRecolectado.toFixed(2)}</strong> · Cascado:{' '}
                <strong>{loading || !kpis ? '…' : kpis.totalCascado.toFixed(2)}</strong> · Tirado:{' '}
                <strong>{loading || !kpis ? '…' : kpis.totalTirado.toFixed(2)}</strong>
              </Typography>
              <Typography variant="body2">
                Índice de rotura/descarte:{' '}
                <strong>{loading || !kpis ? '…' : `${kpis.indiceRoturaPct.toFixed(2)}%`}</strong>
              </Typography>
              {(kpis?.autonomiaAlimento ?? []).map((a) => (
                <Typography key={a.codigoArticulo} variant="body2">
                  MP {a.codigoArticulo}: {a.diasAutonomia} días de autonomía
                </Typography>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default GranjaPanel;
